/**
 * The job page's job mutations: status, schedule, tech, crew, booking
 * confirmation, inline job fields, helper value. Moved out of detail.tsx
 * unchanged (11/3) so the classic page and the new page run the very same
 * code, with the same dispatch gates, warnings and messages.
 */
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { promptDialog } from "@/components/ui/app-dialogs";
import { useAssignmentGuard } from "@/components/dispatch/assignment-warning-dialog";
import { useDispatchPreflight } from "@/lib/dispatch-preflight";
import { needsTestingWorkflow, testingWorkflowPath } from "@/lib/testing-workflow-link";
import { isWoRequired, woRequiredToast } from "@/lib/wo-coming-later";
import type { RailMutations } from "./reference-rail";
import { apiErrorInfo, type AddCrewPayload, type HelperValueTier, type JobDetail } from "./types";

/** Map a job-mutation 409 gate code to a human message; returns null for
 *  non-gate errors so callers can fall back to a generic message. */
export function scheduleGateMessage(code: string | undefined, fallback?: string): string | null {
    switch (code) {
        case "CARD_REQUIRED":
            return fallback ?? "This customer needs a card on file before the job can be dispatched.";
        case "OVERFLOW_LOCKED":
            return fallback ?? "Scheduling is locked during overflow. An admin can override.";
        case "PARTS_NOT_RECEIVED":
            return fallback ?? "Parts for this repair job have not been received yet.";
        case "PARTS_TIMING":
            return fallback ?? "Parts arrive after this dispatch date.";
        case "WO_REQUIRED":
            return fallback ?? "This store sends work orders and this job has none.";
        case "SITE_CHECK_REQUIRED":
            return fallback ?? "Check the site first. Use “Site is right” at the top of this job.";
        default:
            return null;
    }
}

export function useJobMutations(
    id: string | undefined,
    job: JobDetail | null | undefined,
    opts: { onFieldsSaved?: () => void } = {},
) {
    const navigate = useNavigate();
    const qc = useQueryClient();

    const invalidateJob = () => {
        // ["job", id] is a prefix of the Finish Line's key, so this re-reads it.
        qc.invalidateQueries({ queryKey: ["job", id] });
        qc.invalidateQueries({ queryKey: ["jobs"] });
        qc.invalidateQueries({ queryKey: ["dispatch"] });
        qc.invalidateQueries({ queryKey: ["activity-timeline"] });
    };

    // #1728 — with the new job page on, a dispatch of a job whose before-visit
    // promises aren't all kept asks once ("Dispatch anyway?"). A warning, not
    // a gate; with the switch off `confirmDispatch` always says yes.
    const { confirmDispatch } = useDispatchPreflight();

    const updateStatus = useMutation({
        mutationFn: async (status: string) => {
            if (status === "scheduled" && id && !(await confirmDispatch(id, job))) return null;
            return api.patch(`/api/jobs/${id}`, { status });
        },
        onSuccess: (result) => {
            // null = "Fix first": nothing was written.
            if (result === null) return;
            invalidateJob();
        },
        onError: (e: unknown) => {
            const info = apiErrorInfo(e);
            const text = info.message ?? `Status change failed: ${info.error ?? "unknown error"}`;
            // #1601 D1 — "finish Review & Submit" now comes with the door to it.
            if (id && needsTestingWorkflow(info)) {
                toast.error(text, {
                    action: { label: "Open the testing workflow", onClick: () => navigate(testingWorkflowPath(id)) },
                });
                return;
            }
            toast.error(text);
        },
    });

    /** Manual override for the auto-scheduler's helper-value bias on this job. */
    const updateHelperValue = useMutation({
        mutationFn: (value: HelperValueTier | null) => api.patch(`/api/jobs/${id}`, { helperValueOverride: value }),
        onSuccess: () => {
            invalidateJob();
            qc.invalidateQueries({ queryKey: ["dispatch", "autoschedule-preview"] });
            toast.success("Helper value updated");
        },
        onError: () => toast.error("Failed to update helper value"),
    });

    // Reschedule via the canonical PATCH. The server enforces dispatch gates
    // (card on file, parts received, overflow lock) and returns a 409 with an
    // `error` code we translate for the office. The last-submitted vars are
    // kept so the PARTS_NOT_RECEIVED toast can re-submit with force.
    const lastScheduleVars = useRef<{ scheduledStart: string | null; scheduledEnd: string | null } | null>(null);
    const updateSchedule = useMutation({
        mutationFn: async (vars: {
            scheduledStart: string | null;
            scheduledEnd: string | null;
            force?: boolean;
            forceReason?: string;
        }) => {
            lastScheduleVars.current = { scheduledStart: vars.scheduledStart, scheduledEnd: vars.scheduledEnd };
            // A parts-gate retry (force) already passed this check once.
            if (vars.scheduledStart && !vars.force && id && !(await confirmDispatch(id, job))) return null;
            return api.patch(`/api/jobs/${id}`, vars);
        },
        onSuccess: (result) => {
            if (result === null) return;
            invalidateJob();
            toast.success("Schedule updated");
        },
        onError: (e: unknown) => {
            const { error: code, message } = apiErrorInfo(e);
            if (code === "PARTS_NOT_RECEIVED" || code === "PARTS_TIMING") {
                // Both parts gates are overridable, but every override needs
                // a reason — it's ledgered on the blocking PO.
                toast.error(scheduleGateMessage(code, message)!, {
                    action: {
                        label: "Schedule anyway",
                        onClick: async () => {
                            if (!lastScheduleVars.current) return;
                            const reason = await promptDialog({
                                title: "Override the parts gate",
                                label: "Reason (required — ledgered on the PO)",
                                required: true,
                            });
                            if (reason?.trim()) {
                                updateSchedule.mutate({
                                    ...lastScheduleVars.current,
                                    force: true,
                                    forceReason: reason.trim(),
                                });
                            }
                        },
                    },
                });
                return;
            }
            if (isWoRequired(code) && id) {
                woRequiredToast(
                    id,
                    message,
                    () => lastScheduleVars.current && updateSchedule.mutate(lastScheduleVars.current),
                );
                return;
            }
            const gate = scheduleGateMessage(code, message);
            toast.error(gate ?? `Reschedule failed: ${message ?? code ?? "unknown error"}`);
        },
    });

    // Assign / reassign / unassign the primary tech. Same gates apply when
    // assigning a tech (assignedTo != null).
    // #1431: the same warning the Grid shows, on the page where an assignment
    // is just as easy to make. Unassigning skips it — there is nothing to warn
    // about when nobody is being put on the job.
    const { askBeforeAssign, assignmentDialog } = useAssignmentGuard();
    const updateAssignee = useMutation({
        mutationFn: async (assignedTo: string | null) => {
            if (!assignedTo || !id) return api.patch(`/api/jobs/${id}`, { assignedTo });
            if (!(await confirmDispatch(id, job))) return null;
            const decision = await askBeforeAssign(id, assignedTo);
            if (!decision.proceed) return null;
            return api.patch(`/api/jobs/${id}`, {
                assignedTo,
                ...(decision.reason ? { assignmentReason: decision.reason } : {}),
            });
        },
        onSuccess: (result) => {
            // null = the office backed out of the dialog. Nothing was written,
            // so nothing is announced.
            if (result === null) return;
            invalidateJob();
            toast.success("Assignment updated");
        },
        onError: (e: unknown) => {
            const { error: code, message } = apiErrorInfo(e);
            const gate = scheduleGateMessage(code, message);
            toast.error(gate ?? `Assignment failed: ${message ?? code ?? "unknown error"}`);
        },
    });

    // Booking confirmation toggle. A confirmed job is locked from the
    // auto-scheduler so the customer's promised slot won't move.
    const toggleConfirm = useMutation({
        mutationFn: (confirm: boolean) =>
            confirm ? api.post(`/api/jobs/${id}/confirm`) : api.delete(`/api/jobs/${id}/confirm`),
        onSuccess: (_data, confirm) => {
            invalidateJob();
            toast.success(confirm ? "Booking confirmed" : "Booking unconfirmed");
        },
        onError: (e: unknown) => {
            const { error: code, message } = apiErrorInfo(e);
            toast.error(`Failed: ${message ?? code ?? "unknown"}`);
        },
    });

    // Generic PATCH for inline-edited non-dispatch fields (title, job type,
    // tech instructions, scheduling constraints, priority, tags).
    const updateJobFields = useMutation({
        mutationFn: (vars: Record<string, unknown>) => api.patch(`/api/jobs/${id}`, vars),
        onSuccess: () => {
            invalidateJob();
            opts.onFieldsSaved?.();
            toast.success("Job updated");
        },
        onError: (e: unknown) => {
            const { error: code, message } = apiErrorInfo(e);
            const gate = scheduleGateMessage(code, message);
            toast.error(gate ?? `Update failed: ${message ?? code ?? "unknown error"}`);
        },
    });

    const addHelper = useMutation({
        mutationFn: (payload: AddCrewPayload) => api.post(`/api/jobs/${id}/assignments`, payload),
        onSuccess: (_res, payload) => {
            invalidateJob();
            toast.success(
                payload.force
                    ? "Crew member added — crew size raised."
                    : `${payload.role === "tech" ? "Tech" : "Helper"} added to the crew`,
            );
        },
        onError: (e: unknown) => {
            const { error: code, message } = apiErrorInfo(e);
            // CREW_FULL is handled inline by the Schedule & Crew card (the
            // one-tap override prompt) — no dead-end toast.
            if (code === "CREW_FULL") return;
            toast.error(message ?? `Failed: ${code ?? "unknown"}`);
        },
    });

    const removeHelper = useMutation({
        mutationFn: (userId: string) => api.delete<{ voidedPoints?: number }>(`/api/jobs/${id}/assignments/${userId}`),
        onSuccess: (res) => {
            invalidateJob();
            const voided = res?.voidedPoints ?? 0;
            toast.success(
                voided > 0
                    ? `Crew member removed — ${voided} provisional point line${voided === 1 ? "" : "s"} returned.`
                    : "Crew member removed",
            );
        },
        onError: (e: unknown) => {
            const { error: code, message } = apiErrorInfo(e);
            toast.error(message ?? `Failed: ${code ?? "unknown"}`);
        },
    });

    const railMutations: RailMutations = {
        updateAssignee,
        addHelper,
        removeHelper,
        updateJobFields,
        updateSchedule,
        toggleConfirm,
        updateHelperValue,
    };
    return { invalidateJob, updateStatus, railMutations, assignmentDialog };
}
